import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@hooks/useAccessToken';
import { fetchPlayers } from '@api-service/players';

const SEARCH_DEBOUNCE_MS = 300;
const RESULT_LIMIT = 20;
const TWO_MINUTES_IN_MS = 2 * 60 * 1000;
const DEFAULT_SORT_BY = 'predictedPoints';
const DEFAULT_SORT_DIRECTION = 'desc';

export type PlayerSearchItem = {
  value: string;
  label: string;
  team: string;
};

/**
 * Powers the Player In filter's autocomplete. Returns the top-N players by
 * predicted points by default so the dropdown is never empty on focus, and
 * narrows to a server-side search as soon as the user types.
 */
export const usePlayerSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const { accessToken, accessTokenIsFetching } = useAccessToken();

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedQuery(value);
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    debouncedSetQuery(searchQuery.trim());
    return () => debouncedSetQuery.cancel();
  }, [searchQuery, debouncedSetQuery]);

  const { data } = useQuery({
    queryKey: ['playerSearch', debouncedQuery],
    queryFn: () =>
      fetchPlayers({
        ...(accessToken ? { accessToken } : {}),
        ...(debouncedQuery ? { search: debouncedQuery } : {}),
        sortBy: DEFAULT_SORT_BY,
        sortDirection: DEFAULT_SORT_DIRECTION,
        limit: RESULT_LIMIT
      }),
    enabled: !accessTokenIsFetching,
    staleTime: TWO_MINUTES_IN_MS,
    refetchOnWindowFocus: false
  });

  const searchItems: PlayerSearchItem[] =
    data?.data.map((player) => ({
      value: player.id,
      label: player.displayName,
      team: player.team.shortName
    })) ?? [];

  return {
    searchItems,
    onQueryChange: setSearchQuery
  };
};
